import { GraduationCap, Mail, MapPin, Phone } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguagePicker } from '@/features/preferences/components/PreferenceControls'
import { APP_NAME } from '@/utils/brand'
import type { PublicAdmissions } from '../types'

/**
 * Public pages of online admissions: the school's name, logo and colours (as on the sign-in page),
 * the language switch, and the school's contact details at the bottom. Mobile first.
 */
export function PublicAdmissionShell({
  school,
  title,
  subtitle,
  children,
}: {
  school: PublicAdmissions['school'] | null
  title: string
  subtitle?: ReactNode
  children: ReactNode
}) {
  const { t } = useTranslation('admissions')
  return (
    <div className="min-h-dvh bg-canvas">
      <header className="bg-brand-gradient relative overflow-hidden text-white">
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 size-72 rounded-full bg-white/10 blur-2xl"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-20 -left-16 size-56 rounded-full bg-accent-400/30 blur-3xl"
        />
        <div className="relative mx-auto flex max-w-3xl min-w-0 items-center gap-3 px-4 pt-4 sm:px-6">
          <a href="/" className="flex min-w-0 flex-1 items-center gap-3">
            <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-xl bg-white/15 ring-1 ring-white/25">
              {school?.logoUrl ? (
                <img src={school.logoUrl} alt="" className="size-full object-contain" />
              ) : (
                <GraduationCap className="size-5" aria-hidden="true" />
              )}
            </span>
            <span className="min-w-0 text-sm leading-tight font-semibold break-words">
              {school?.name ?? APP_NAME}
            </span>
          </a>
          <div className="w-24 shrink-0">
            <LanguagePicker compact className="border-white/25 bg-white/10" />
          </div>
        </div>
        <div className="relative mx-auto max-w-3xl px-4 pt-6 pb-8 sm:px-6 sm:pb-10">
          <h1 className="text-2xl leading-tight font-semibold break-words sm:text-3xl">{title}</h1>
          {subtitle ? <div className="mt-2 text-sm text-white/85">{subtitle}</div> : null}
        </div>
      </header>

      <main className="mx-auto -mt-4 max-w-3xl min-w-0 px-3 pb-10 sm:px-6">{children}</main>

      {school && (school.phone || school.email || school.address) ? (
        <footer className="mx-auto max-w-3xl px-4 pb-10 text-sm text-muted sm:px-6">
          <p className="mb-2 font-medium text-ink-soft">{t('School office')}</p>
          <ul className="space-y-1.5">
            {school.phone ? (
              <li className="flex items-center gap-2">
                <Phone className="size-4 shrink-0" aria-hidden="true" />
                <a href={`tel:${school.phone.replace(/\s/g, '')}`} className="hover:text-ink">
                  {school.phone}
                </a>
              </li>
            ) : null}
            {school.email ? (
              <li className="flex min-w-0 items-center gap-2">
                <Mail className="size-4 shrink-0" aria-hidden="true" />
                <a href={`mailto:${school.email}`} className="min-w-0 break-all hover:text-ink">
                  {school.email}
                </a>
              </li>
            ) : null}
            {school.address ? (
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span className="whitespace-pre-line">{school.address}</span>
              </li>
            ) : null}
          </ul>
        </footer>
      ) : null}
    </div>
  )
}
